// AI Services API functions for LogiqOS
import { getSessionUUID } from '../components/lib/AuthToken';
import logger from '../utils/logger';

/**
 * Get AI-powered dashboard insights
 */
export const getAIDashboardInsights = async () => {
    try {
        const sessionToken = getSessionUUID();

        const response = await fetch('/ai/insights', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return {
                success: true,
                insights: data.insights,
                status: response.status
            };
        } else {
            throw new Error(data.error || 'Failed to fetch AI insights');
        }
    } catch (error) {
        logger.error('Error fetching AI dashboard insights:', error);
        return {
            success: false,
            error: error.message,
            status: 500
        };
    }
};

/**
 * Send message to AI chatbot
 */
export const sendChatbotMessage = async (message, chatHistory = []) => {
    try {
        const sessionToken = getSessionUUID();

        // Send the last 10 turns (5 exchanges) as belt-and-braces alongside the Flask session
        const historyPayload = chatHistory
            .slice(-10)
            .map(msg => ({
                role: msg.type === 'user' ? 'user' : 'assistant',
                content: typeof msg.content === 'string' ? msg.content : msg.content?.message || ''
            }))
            .filter(turn => turn.content);

        const response = await fetch('/ai/chat', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ message, chat_history: historyPayload })
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return {
                success: true,
                response: data.response,
                intent: data.response.intent,
                confidence: data.response.confidence,
                type: data.response.type,
                status: response.status
            };
        } else {
            throw new Error(data.error || 'Failed to process message');
        }
    } catch (error) {
        logger.error('Error sending chatbot message:', error);
        return {
            success: false,
            error: error.message,
            status: 500
        };
    }
};

/**
 * Get chatbot quick actions
 */
export const getChatbotQuickActions = async () => {
    try {
        const sessionToken = getSessionUUID();
        
        const response = await fetch('/ai/chatbot/quick-actions', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return {
                success: true,
                quickActions: data.quick_actions,
                userType: data.user_type,
                status: response.status
            };
        } else {
            throw new Error(data.error || 'Failed to fetch quick actions');
        }
    } catch (error) {
        logger.error('Error fetching chatbot quick actions:', error);
        return {
            success: false,
            error: error.message,
            status: 500
        };
    }
};

/**
 * Get vehicle performance analytics
 */
export const getVehiclePerformance = async (vehicleId = null, timeRange = '7d') => {
    try {
        const sessionToken = getSessionUUID();
        const params = new URLSearchParams({ time_range: timeRange });
        if (vehicleId) params.append('vehicle_id', vehicleId);
        
        const response = await fetch(`/ai/analytics/vehicle-performance?${params}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return {
                success: true,
                analytics: data.analytics,
                status: response.status
            };
        } else {
            throw new Error(data.error || 'Failed to fetch vehicle performance');
        }
    } catch (error) {
        logger.error('Error fetching vehicle performance:', error);
        return {
            success: false,
            error: error.message,
            status: 500
        };
    }
};

/**
 * Get warehouse trends analytics
 */
export const getWarehouseTrends = async (timeRange = '30d') => {
    try {
        const sessionToken = getSessionUUID();
        const params = new URLSearchParams({ time_range: timeRange });
        
        const response = await fetch(`/ai/analytics/warehouse-trends?${params}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return {
                success: true,
                trends: data.trends,
                status: response.status
            };
        } else {
            throw new Error(data.error || 'Failed to fetch warehouse trends');
        }
    } catch (error) {
        logger.error('Error fetching warehouse trends:', error);
        return {
            success: false,
            error: error.message,
            status: 500
        };
    }
};

/**
 * Get maintenance predictions
 */
export const getMaintenancePredictions = async () => {
    try {
        const sessionToken = getSessionUUID();
        
        const response = await fetch('/ai/analytics/maintenance-predictions', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return {
                success: true,
                predictions: data.predictions,
                status: response.status
            };
        } else {
            throw new Error(data.error || 'Failed to fetch maintenance predictions');
        }
    } catch (error) {
        logger.error('Error fetching maintenance predictions:', error);
        return {
            success: false,
            error: error.message,
            status: 500
        };
    }
};

/**
 * Get efficiency recommendations
 */
export const getEfficiencyRecommendations = async () => {
    try {
        const sessionToken = getSessionUUID();
        
        const response = await fetch('/ai/analytics/efficiency-recommendations', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return {
                success: true,
                recommendations: data.recommendations,
                status: response.status
            };
        } else {
            throw new Error(data.error || 'Failed to fetch efficiency recommendations');
        }
    } catch (error) {
        logger.error('Error fetching efficiency recommendations:', error);
        return {
            success: false,
            error: error.message,
            status: 500
        };
    }
};

/**
 * Error handling utility
 */
export const handleAIError = (error) => {
    logger.error('AI Services Error:', error);
    
    if (error.message.includes('404')) {
        return {
            success: false,
            message: 'AI service temporarily unavailable. Please try again later.',
            error_type: 'service_unavailable'
        };
    }
    
    if (error.message.includes('429')) {
        return {
            success: false,
            message: 'Too many requests. Please wait a moment before trying again.',
            error_type: 'rate_limit'
        };
    }
    
    if (error.message.includes('500')) {
        return {
            success: false,
            message: 'Internal server error. Our team has been notified.',
            error_type: 'server_error'
        };
    }
    
    return {
        success: false,
        message: 'Connection error. Please check your internet connection.',
        error_type: 'network_error'
    };
};

/**
 * Cache utility for analytics data
 */
class AIApiCache {
    constructor(ttl = 5 * 60 * 1000) { // 5 minutes default TTL
        this.cache = new Map();
        this.ttl = ttl;
    }
    
    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;
        
        if (Date.now() > item.expiry) {
            this.cache.delete(key);
            return null;
        }
        
        return item.data;
    }
    
    set(key, data) {
        this.cache.set(key, {
            data,
            expiry: Date.now() + this.ttl
        });
    }
    
    clear() {
        this.cache.clear();
    }
}

// Create cache instance
export const aiCache = new AIApiCache();

// Feedback functionality removed - new AI Orchestrator doesn't need feedback

/**
 * Cached API call wrapper
 */
export const cachedAICall = async (cacheKey, apiFunction, forceRefresh = false) => {
    if (!forceRefresh) {
        const cached = aiCache.get(cacheKey);
        if (cached) {
            return cached;
        }
    }
    
    try {
        const result = await apiFunction();
        if (result.success) {
            aiCache.set(cacheKey, result);
        }
        return result;
    } catch (error) {
        // If cache exists and API fails, return stale data with warning
        const staleData = aiCache.get(cacheKey);
        if (staleData) {
            logger.warn('AI API failed, returning stale data:', error);
            return {
                ...staleData,
                _stale: true,
                _warning: 'This data may be outdated due to connection issues.'
            };
        }
        throw error;
    }
};

// New AI Orchestrator endpoints
export const getAvailableIntents = async () => {
    try {
        const response = await fetch('/ai/intents', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching AI intents:', error);
        throw error;
    }
};

export const classifyQuestion = async (question) => {
    try {
        const response = await fetch('/ai/classify', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ question })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error classifying question:', error);
        throw error;
    }
};